import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  authService,
  propertiesService,
  roomsService,
  coursesService,
  addonsService,
  bookingsService,
  availabilityService,
  usersService,
  auditService,
  healthService
} from '../services/api';

function ApiTester({ user }) {
  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [requestData, setRequestData] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);

  // Image service functions
  const imageService = {
    uploadSingle: async (formData) => {
      const response = await fetch('/api/images/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      return response.json();
    },
    uploadMultiple: async (formData) => {
      const response = await fetch('/api/images/upload-multiple', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      return response.json();
    },
    getImages: async (entityType, entityId) => {
      const response = await fetch(`/api/images/${entityType}/${entityId}`);
      return response.json();
    },
    updateImage: async (imageId, description) => {
      const response = await fetch(`/api/images/${imageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ description })
      });
      return response.json();
    },
    deleteImage: async (imageId) => {
      const response = await fetch(`/api/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.json();
    }
  };

  const apiEndpoints = {
    'Health Check': {
      'GET /health': () => healthService.check()
    },
    'Authentication': {
      'GET /api/auth/me': () => authService.getCurrentUser(),
      'POST /api/auth/logout': () => authService.logout()
    },
    'Properties': {
      'GET /api/properties': () => propertiesService.getAll(),
      'GET /api/properties/{id}': () => propertiesService.getById('main-property'),
      'POST /api/properties': () => propertiesService.create(JSON.parse(requestData || '{"name":"Test Property","address":"Test Address"}'))
    },
    'Rooms': {
      'GET /api/rooms': () => roomsService.getAll(),
      'GET /api/rooms/{id}': () => roomsService.getById('private-room-1'),
      'POST /api/rooms': () => roomsService.create(JSON.parse(requestData || '{"roomNumber":"T1","roomTypeId":"private-room","propertyId":"main-property"}'))
    },
    'Courses': {
      'GET /api/courses': () => coursesService.getAll(),
      'GET /api/courses/{id}': () => coursesService.getById('full-moon-festival'),
      'POST /api/courses': () => coursesService.create(JSON.parse(requestData || '{"name":"Test Course","description":"Test Description","startDate":"2025-08-01T00:00:00Z","endDate":"2025-08-05T00:00:00Z"}'))
    },
    'Add-ons': {
      'GET /api/addons': () => addonsService.getAll(),
      'GET /api/addons/{id}': () => addonsService.getById('spa-service'),
      'POST /api/addons': () => addonsService.create(JSON.parse(requestData || '{"name":"Test Service","description":"Test Description","price":25.00}'))
    },
    'Images': {
      'POST /api/images/upload': () => {
        const formData = new FormData();
        if (selectedFiles.length > 0) {
          formData.append('image', selectedFiles[0]);
        }
        const data = JSON.parse(requestData || '{}');
        Object.keys(data).forEach(key => {
          formData.append(key, data[key]);
        });
        return imageService.uploadSingle(formData);
      },
      'POST /api/images/upload-multiple': () => {
        const formData = new FormData();
        selectedFiles.forEach(file => {
          formData.append('images', file);
        });
        const data = JSON.parse(requestData || '{}');
        Object.keys(data).forEach(key => {
          formData.append(key, data[key]);
        });
        return imageService.uploadMultiple(formData);
      },
      'GET /api/images/course/{id}': () => imageService.getImages('course', 'full-moon-festival'),
      'GET /api/images/room/{id}': () => imageService.getImages('room', 'private-room-1'),
      'GET /api/images/property/{id}': () => imageService.getImages('property', 'main-property'),
      'PUT /api/images/{id}': () => imageService.updateImage('test-image-id', 'Updated description'),
      'DELETE /api/images/{id}': () => imageService.deleteImage('test-image-id')
    },
    'Bookings': {
      'GET /api/bookings': () => bookingsService.getAll(),
      'POST /api/bookings': () => bookingsService.create(JSON.parse(requestData || '{"roomId":"private-room-1","checkIn":"2025-08-01T15:00:00Z","checkOut":"2025-08-03T11:00:00Z","totalPrice":200.00}')),
      'GET /api/admin/booking-details/{id}': async () => {
        // This endpoint returns booking details with price breakdowns
        const response = await fetch('/api/admin/booking-details/OH-1', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        return response.json();
      }
    },
    'Price Breakdowns': {
      'GET /api/price-breakdowns': async () => {
        const response = await fetch('/api/price-breakdowns', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        return response.json();
      },
      'GET /api/price-breakdowns/roomtype/{id}': async () => {
        const response = await fetch('/api/price-breakdowns/roomtype/cmf0wrliy003b5lgea8eft2s3', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          }
        });
        return response.json();
      },
      'POST /api/price-breakdowns': async () => {
        const sampleData = JSON.parse(requestData || '{"name":"Room Rent","amount":3500.0,"roomTypeId":"cmf0wrliy003b5lgea8eft2s3","isActive":true}');
        const response = await fetch('/api/price-breakdowns', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(sampleData)
        });
        return response.json();
      }
    },
    'Availability': {
      'GET /api/availability': () => availabilityService.check({
        checkIn: '2025-08-01T15:00:00Z',
        checkOut: '2025-08-03T11:00:00Z'
      }),
      'GET /api/availability/room/{id}': () => availabilityService.checkRoom('private-room-1', {
        checkIn: '2025-08-01T15:00:00Z',
        checkOut: '2025-08-03T11:00:00Z'
      })
    },
    'Users': {
      'GET /api/users': () => usersService.getAll(),
      'GET /api/users/{id}/bookings': () => usersService.getUserBookings(user?.id || 'user-id')
    },
    'Audit': {
      'GET /api/audit': () => auditService.getAll(),
      'GET /api/audit/stats': () => auditService.getStats()
    }
  };

  const handleEndpointSelect = (endpoint) => {
    setSelectedEndpoint(endpoint);
    setResponse(null);
    setRequestData('');
    setSelectedFiles([]);
  };

  const handleFileSelect = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const executeEndpoint = async () => {
    if (!selectedEndpoint) return;

    setLoading(true);
    try {
      const [category, endpoint] = selectedEndpoint.split(' - ');
      const endpointFunction = apiEndpoints[category]?.[endpoint];
      
      if (endpointFunction) {
        const result = await endpointFunction();
        setResponse({
          success: true,
          data: result,
          timestamp: new Date().toISOString()
        });
        toast.success('API call successful');
      } else {
        throw new Error('Endpoint not found');
      }
    } catch (error) {
      setResponse({
        success: false,
        error: error.message || 'API call failed',
        details: error,
        timestamp: new Date().toISOString()
      });
      toast.error('API call failed');
    } finally {
      setLoading(false);
    }
  };

  const generateSampleData = () => {
    const samples = {
      'POST /api/properties': {
        name: 'New Property',
        address: '123 Test Street, Test City'
      },
      'POST /api/rooms': {
        roomNumber: 'T001',
        roomTypeId: 'private-room',
        propertyId: 'main-property',
        status: 'AVAILABLE'
      },
      'POST /api/courses': {
        name: 'Test Meditation Course',
        description: 'A test course for API testing',
        startDate: '2025-09-01T00:00:00Z',
        endDate: '2025-09-05T00:00:00Z'
      },
      'POST /api/addons': {
        name: 'Test Add-on Service',
        description: 'A test additional service',
        price: 50.00
      },
      'POST /api/bookings': {
        roomId: 'private-room-1',
        checkIn: '2025-09-01T15:00:00Z',
        checkOut: '2025-09-03T11:00:00Z',
        totalPrice: 300.00,
        guestDetails: {
          name: 'Test Guest',
          email: 'test@example.com'
        }
      },
      'POST /api/images/upload': {
        description: 'Test image description',
        roomId: 'private-room-1'
      },
      'POST /api/images/upload-multiple': {
        description: 'Multiple test images',
        courseId: 'full-moon-festival'
      },
      'POST /api/price-breakdowns': {
        name: 'Room Rent',
        amount: 3500.0,
        roomTypeId: 'cmf0wrliy003b5lgea8eft2s3',
        isActive: true
      }
    };

    const endpoint = selectedEndpoint.split(' - ')[1];
    const sample = samples[endpoint];
    if (sample) {
      setRequestData(JSON.stringify(sample, null, 2));
    }
  };

  const isImageUploadEndpoint = selectedEndpoint.includes('upload');

  return (
    <div>
      <h2>API Tester</h2>
      <p>Test all API endpoints directly from the frontend. Select an endpoint and execute it to see the response.</p>

      <div className="grid grid-2">
        <div className="card">
          <h3>Select API Endpoint</h3>
          
          {Object.entries(apiEndpoints).map(([category, endpoints]) => (
            <div key={category} style={{ marginBottom: '20px' }}>
              <h4 style={{ color: '#1A3E2A', marginBottom: '10px' }}>{category}</h4>
              {Object.keys(endpoints).map(endpoint => (
                <button
                  key={endpoint}
                  className={`btn ${selectedEndpoint === `${category} - ${endpoint}` ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => handleEndpointSelect(`${category} - ${endpoint}`)}
                  style={{ 
                    display: 'block', 
                    width: '100%', 
                    marginBottom: '5px',
                    textAlign: 'left',
                    fontSize: '14px'
                  }}
                >
                  {endpoint}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="card">
          <h3>Request & Response</h3>
          
          {selectedEndpoint && (
            <div>
              <h4>Selected: {selectedEndpoint}</h4>
              
              {isImageUploadEndpoint && (
                <div className="form-group">
                  <label>Select Image Files:</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple={selectedEndpoint.includes('multiple')}
                    onChange={handleFileSelect}
                    className="form-control"
                  />
                  {selectedFiles.length > 0 && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#5F6B61' }}>
                      Selected: {selectedFiles.map(f => f.name).join(', ')}
                    </div>
                  )}
                </div>
              )}

              {(selectedEndpoint.includes('POST') || selectedEndpoint.includes('PUT')) && (
                <div className="form-group">
                  <label>{isImageUploadEndpoint ? 'Additional Data (JSON):' : 'Request Body (JSON):'}</label>
                  <textarea
                    className="form-control"
                    rows="8"
                    value={requestData}
                    onChange={(e) => setRequestData(e.target.value)}
                    placeholder={isImageUploadEndpoint ? 
                      'Enter additional data like description, entityId, etc...' : 
                      'Enter JSON request body...'
                    }
                  />
                  <button
                    className="btn btn-secondary mt-2"
                    onClick={generateSampleData}
                    style={{ fontSize: '12px' }}
                  >
                    Generate Sample Data
                  </button>
                </div>
              )}

              <div style={{ marginTop: '15px' }}>
                <button
                  className="btn btn-primary"
                  onClick={executeEndpoint}
                  disabled={loading || (isImageUploadEndpoint && selectedFiles.length === 0)}
                >
                  {loading ? 'Executing...' : 'Execute API Call'}
                </button>
              </div>

              {isImageUploadEndpoint && selectedFiles.length === 0 && (
                <div style={{ marginTop: '10px', color: '#8E3628', fontSize: '12px' }}>
                  Please select at least one image file to upload.
                </div>
              )}
            </div>
          )}

          {response && (
            <div style={{ marginTop: '20px' }}>
              <h4>Response:</h4>
              <div 
                style={{
                  backgroundColor: response.success ? '#E1E9E2' : '#E5B3A9',
                  border: `1px solid ${response.success ? '#C4D4C8' : '#E5B3A9'}`,
                  borderRadius: '4px',
                  padding: '15px'
                }}
              >
                <div style={{ marginBottom: '10px' }}>
                  <strong>Status:</strong> {response.success ? 'Success' : 'Error'}
                  <br />
                  <strong>Timestamp:</strong> {response.timestamp}
                </div>
                {/* Special formatting for price breakdown responses */}
                {response.success && selectedEndpoint?.includes('price-breakdowns') && response.data?.priceBreakdowns && (
                  <div style={{ marginBottom: '15px' }}>
                    <h5 style={{ color: '#1A3E2A', marginBottom: '10px' }}>Price Breakdown Summary</h5>
                    <div style={{ 
                      backgroundColor: response.data.isComplete ? '#E1E9E2' : '#F3E6CB',
                      border: `1px solid ${response.data.isComplete ? '#C4D4C8' : '#E8CF9C'}`,
                      borderRadius: '4px',
                      padding: '12px',
                      marginBottom: '10px'
                    }}>
                      <div><strong>Total Amount:</strong> ₹{response.data.totalAmount?.toFixed(2) || '0.00'}</div>
                      <div><strong>Status:</strong> {response.data.isComplete ? 'Complete' : 'Incomplete'}</div>
                      <div><strong>Breakdown Count:</strong> {response.data.priceBreakdowns.length}</div>
                    </div>
                    <div>
                      <h6 style={{ marginBottom: '8px' }}>Breakdown Details:</h6>
                      {response.data.priceBreakdowns.map((breakdown, idx) => (
                        <div key={idx} style={{
                          backgroundColor: breakdown.isActive ? '#E1E9E2' : '#F5F3EB',
                          border: `1px solid ${breakdown.isActive ? '#163626' : '#5F6B61'}`,
                          borderRadius: '4px',
                          padding: '8px',
                          marginBottom: '5px',
                          fontSize: '12px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold' }}>{breakdown.name}</span>
                            <div>
                              <span style={{ color: '#163626', fontWeight: 'bold' }}>₹{breakdown.amount?.toFixed(2) || '0.00'}</span>
                              <span style={{ 
                                marginLeft: '8px',
                                backgroundColor: breakdown.isActive ? '#163626' : '#5F6B61',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '10px'
                              }}>
                                {breakdown.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                          <div style={{ fontSize: '10px', color: '#5F6B61', marginTop: '4px' }}>
                            Created: {new Date(breakdown.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Special formatting for booking details with price breakdowns */}
                {response.success && selectedEndpoint?.includes('booking-details') && response.data?.pricing?.priceBreakdowns && (
                  <div style={{ marginBottom: '15px' }}>
                    <h5 style={{ color: '#1A3E2A', marginBottom: '10px' }}>Booking Price Breakdown</h5>
                    <div style={{ 
                      backgroundColor: '#F5F3EB',
                      border: '1px solid #E3DFD3',
                      borderRadius: '4px',
                      padding: '12px',
                      marginBottom: '10px'
                    }}>
                      <div><strong>Total Price:</strong> ₹{response.data.pricing.totalPrice?.toFixed(2) || '0.00'}</div>
                      <div><strong>Breakdown Summary:</strong> ₹{response.data.pricing.priceBreakdownSummary?.totalAmount?.toFixed(2) || '0.00'} ({response.data.pricing.priceBreakdownSummary?.isComplete ? 'Complete' : 'Incomplete'})</div>
                    </div>
                    <div>
                      <h6 style={{ marginBottom: '8px' }}>Price Components:</h6>
                      {response.data.pricing.priceBreakdowns.map((breakdown, idx) => (
                        <div key={idx} style={{
                          backgroundColor: '#E1E9E2',
                          border: '1px solid #163626',
                          borderRadius: '4px',
                          padding: '8px',
                          marginBottom: '5px',
                          fontSize: '12px'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold' }}>{breakdown.name}</span>
                            <div>
                              <span style={{ color: '#163626', fontWeight: 'bold' }}>
                                ₹{breakdown.amount?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <pre style={{ 
                  backgroundColor: '#F5F3EB',
                  padding: '10px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '12px'
                }}>
                  {JSON.stringify(response.success ? response.data : response.error, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {!user && (
            <div className="error" style={{ marginTop: '20px' }}>
              <strong>Note:</strong> You are not logged in. Some endpoints may require authentication.
              <br />
              <a href="/login">Login here</a> to test authenticated endpoints.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ApiTester;